// useDisplayPrefsManager.ts
// Custom hook to manage display preferences with persistence

import { useCallback } from 'react';
import { DisplayPrefs } from '../types';
import { localDB } from '../../../../../crud/localDB/localDB';

interface UseDisplayPrefsManagerProps {
  displayPrefs: DisplayPrefs;
  setDisplayPrefs: (prefs: DisplayPrefs) => void;
}

interface UseDisplayPrefsManagerReturn {
  updateDisplayPrefs: (next: DisplayPrefs) => Promise<void>;
}

export const useDisplayPrefsManager = ({
  displayPrefs,
  setDisplayPrefs,
}: UseDisplayPrefsManagerProps): UseDisplayPrefsManagerReturn => {
  
  const updateDisplayPrefs = useCallback(async (next: DisplayPrefs) => {
    // Enforce mutual exclusion for number/tag
    let safe: DisplayPrefs;
    
    if (next.showNumber && next.showTag) {
      // If both are true, keep only number
      safe = {
        showNumber: true,
        showTag: false,
        showElapsed: next.showElapsed !== false,
        showCharges: next.showCharges !== false,
        showWaiter: next.showWaiter !== false,
        showPeopleType: next.showPeopleType || 'capacity',
      };
    } else if (!next.showNumber && !next.showTag) {
      // If both are false, enable number
      safe = {
        showNumber: true,
        showTag: false,
        showElapsed: next.showElapsed !== false,
        showCharges: next.showCharges !== false,
        showWaiter: next.showWaiter !== false,
        showPeopleType: next.showPeopleType || 'capacity',
      };
    } else {
      // One is true, one is false - perfect
      safe = {
        showNumber: next.showNumber,
        showTag: next.showTag,
        showElapsed: next.showElapsed !== false,
        showCharges: next.showCharges !== false,
        showWaiter: next.showWaiter !== false,
        showPeopleType: next.showPeopleType || 'capacity',
      };
    }
    
    setDisplayPrefs(safe);
    
    try {
      await localDB.set('restaurant', 'displayPrefs', safe as any);
    } catch (error) {
      console.error('Failed to persist display preferences:', error);
    }
  }, [setDisplayPrefs]);

  return {
    updateDisplayPrefs,
  };
};
